import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { dsConfirm } from "../../utils/ds_confirm";

export default class extends Controller {
  static targets = [
    "filtersPanel", "filtersForm", "filtersToggle",
    "attendancesList", "modal", "modalTitle",
    "attendanceForm", "attendanceId", "teacherSelect",
    "statusSelect", "dateInput", "timeInput", "notesInput",
    "submitText"
  ];

  static values = {
    schoolId: String,
    titleNew: String,
    titleEdit: String,
    submitNew: String,
    submitEdit: String,
    confirmDelete: String
  };

  connect() {
    this.body = document.body;
  }

  // --- Filtres ---

  toggleFilters() {
    this.filtersPanelTarget.classList.toggle('hidden');
    this.filtersToggleTarget.classList.toggle('btn-primary');
  }

  applyFilters(event) {
    if (event) event.preventDefault();

    const formData = new FormData(this.filtersFormTarget);
    const params = new URLSearchParams(formData);

    fetch(`${window.location.pathname}?${params.toString()}`, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html);
    })
    .catch(error => {
      console.error('Error applying filters:', error);
    });
  }

  resetFilters() {
    this.filtersFormTarget.reset();
    this.applyFilters();
  }

  refreshTable() {
    this.applyFilters();
  }

  // --- Modal Management ---

  showCreateForm() {
    this.resetForm();
    this.modalTitleTarget.textContent = this.titleNewValue || 'Add attendance';
    this.submitTextTarget.textContent = this.submitNewValue || 'Add';
    if (this.hasTeacherSelectTarget) this.teacherSelectTarget.disabled = false;
    this.openModal();
  }

  showEditForm(event) {
    this.resetForm();
    const btn = event.currentTarget;
    const row = btn.closest('tr');
    if (!row) {
      console.error('Could not find parent row for edit');
      return;
    }
    
    // Debug: log all data attributes
    console.log('=== Edit Form Debug ===');
    console.log('Row dataset:', row.dataset);
    console.log('Teacher ID:', row.dataset.teacherId);
    console.log('Status:', row.dataset.status);
    console.log('Notes:', row.dataset.notes);
    console.log('Pointed At:', row.dataset.pointedAt);
    
    // Populate form from row data attributes
    if (this.hasAttendanceIdTarget) {
      this.attendanceIdTarget.value = btn.dataset.attendanceId || row.dataset.attendanceId;
      console.log('Set attendanceId:', this.attendanceIdTarget.value);
    }
    
    if (this.hasTeacherSelectTarget) {
      const teacherId = row.dataset.teacherId || '';
      console.log('Setting teacher select to:', teacherId);
      console.log('Available teacher options:', Array.from(this.teacherSelectTarget.options).map(o => ({value: o.value, text: o.text})));
      
      // Find the option by value and set selectedIndex
      const teacherOptions = this.teacherSelectTarget.options;
      for (let i = 0; i < teacherOptions.length; i++) {
        if (teacherOptions[i].value === teacherId) {
          this.teacherSelectTarget.selectedIndex = i;
          console.log('Teacher option found at index:', i, 'text:', teacherOptions[i].text);
          break;
        }
      }
      console.log('Teacher select value after set:', this.teacherSelectTarget.value);
      // Make it visually readonly
      this.teacherSelectTarget.style.pointerEvents = 'none';
      this.teacherSelectTarget.style.opacity = '0.7';
    }
    
    if (this.hasStatusSelectTarget) {
      const status = row.dataset.status || '';
      console.log('Setting status select to:', status);
      console.log('Available status options:', Array.from(this.statusSelectTarget.options).map(o => ({value: o.value, text: o.text})));
      
      // Find the option by value and set selectedIndex
      const statusOptions = this.statusSelectTarget.options;
      for (let i = 0; i < statusOptions.length; i++) {
        if (statusOptions[i].value === status) {
          this.statusSelectTarget.selectedIndex = i;
          console.log('Status option found at index:', i, 'text:', statusOptions[i].text);
          break;
        }
      }
      console.log('Status select value after set:', this.statusSelectTarget.value);
    }
    
    if (this.hasNotesInputTarget) {
      this.notesInputTarget.value = row.dataset.notes || '';
      console.log('Set notes to:', this.notesInputTarget.value);
    }
    
    const pointedAtStr = row.dataset.pointedAt;
    if (pointedAtStr && this.hasDateInputTarget && this.hasTimeInputTarget) {
      const date = new Date(pointedAtStr);
      console.log('Parsed date:', date, 'isValid:', !isNaN(date.getTime()));
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        console.log('Setting date to:', dateStr);
        this.dateInputTarget.value = dateStr;
        
        // For flatpickr, we may need to use setDate
        if (this.dateInputTarget._flatpickr) {
          this.dateInputTarget._flatpickr.setDate(dateStr, false);
        }
        
        const hours = String(date.getHours()).padStart(2, '0');
        const mins = String(date.getMinutes()).padStart(2, '0');
        const timeStr = `${hours}:${mins}`;
        console.log('Setting time to:', timeStr);
        this.timeInputTarget.value = timeStr;
        
        // For flatpickr time
        if (this.timeInputTarget._flatpickr) {
          this.timeInputTarget._flatpickr.setDate(timeStr, false);
        }
      }
    }

    this.modalTitleTarget.textContent = this.titleEditValue || 'Edit attendance';
    this.submitTextTarget.textContent = this.submitEditValue || 'Update';
    this.openModal();
    console.log('=== Edit Form Debug End ===');
  }

  // Create: PATCH update_attendance (teacher_name, status, justification,
  // pointed_at). Edit: PATCH update_pointing?id= (teacher_attendance_pointing[...]).
  // Both are routes of config/routes/schools_rh.rb; the form's own field names
  // (school_staff_id, notes, pointed_date, pointed_time) are read by neither.
  submitForm(event) {
    event.preventDefault();

    const fields = new FormData(this.attendanceFormTarget);
    const attendanceId = this.hasAttendanceIdTarget ? this.attendanceIdTarget.value : '';
    const pointedAt = this.pointedAtFrom(fields);
    const body = new FormData();
    let url;
    let accept;

    if (attendanceId) {
      url = window.location.pathname.replace('/management', `/update_pointing?id=${encodeURIComponent(attendanceId)}`);
      accept = 'application/json';
      body.append('teacher_attendance_pointing[status]', fields.get('status') || '');
      body.append('teacher_attendance_pointing[notes]', fields.get('notes') || '');
      if (pointedAt) body.append('teacher_attendance_pointing[pointed_at]', pointedAt);
    } else {
      url = window.location.pathname.replace('/management', '/update_attendance');
      accept = 'text/vnd.turbo-stream.html, application/json';
      body.append('teacher_name', fields.get('school_staff_id') || '');
      body.append('status', fields.get('status') || '');
      body.append('justification', fields.get('notes') || '');
      if (pointedAt) body.append('pointed_at', pointedAt);
    }

    fetch(url, {
      method: 'PATCH',
      body: body,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': accept
      }
    })
    .then(response => response.text().then(text => {
      if (response.ok) {
        if (text.includes('turbo-stream')) {
          Turbo.renderStreamMessage(text);
        } else {
          this.applyFilters();
        }
        this.closeModal();
        return;
      }

      try {
        const data = JSON.parse(text);
        alert(data.errors ? data.errors.join(', ') : 'Erreur lors de l\'enregistrement');
      } catch (e) {
        alert('Erreur lors de l\'enregistrement');
      }
    }))
    .catch(error => {
      console.error('Error submitting form:', error);
    });
  }

  // "2026-10-15T08:05:00" from the date and time fields, or null.
  pointedAtFrom(fields) {
    const date = fields.get('pointed_date');
    const time = fields.get('pointed_time');
    if (!date) return null;

    return `${date}T${time || '00:00'}:00`;
  }

  async deleteAttendance(event) {
    const btn = event.currentTarget;
    const id = btn.dataset.attendanceId;
    if (!id) {
      console.error('No attendance ID found for deletion');
      return;
    }
    
    if (!(await dsConfirm(this.confirmDeleteValue || 'Are you sure you want to delete this attendance?', { tone: "danger" }))) return;

    try {
      // Use current path to get locale dynamically
      const basePath = window.location.pathname.replace('/management', '/delete_attendance');
      const response = await fetch(`${basePath}?id=${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
      } else {
        alert('Erreur lors de la suppression');
        console.error('Delete failed:', response.statusText);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Une erreur est survenue lors de la suppression');
    }
  }

  exportData() {
    const formData = new FormData(this.filtersFormTarget);
    const params = new URLSearchParams(formData);
    params.append('format', 'csv');
    
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  }

  // --- Helpers ---

  openModal() {
    this.modalTarget.classList.remove('hidden');
    this.body.classList.add('overflow-hidden');
  }

  closeModal() {
    this.modalTarget.classList.add('hidden');
    this.body.classList.remove('overflow-hidden');
  }

  resetForm() {
    this.attendanceFormTarget.reset();
    if (this.hasAttendanceIdTarget) this.attendanceIdTarget.value = '';
    if (this.hasTeacherSelectTarget) {
      this.teacherSelectTarget.style.pointerEvents = '';
      this.teacherSelectTarget.style.opacity = '';
    }
  }

  t(key, options = {}) {
    if (typeof window !== "undefined" && typeof window.t === "function") {
      return window.t(key, options);
    }
    return options.default || key;
  }
}
